import { FC, ReactNode, useEffect, useState } from 'react';
import {
  TextField,
  OutlinedTextFieldProps,
  InputAdornment,
  IconButton,
  MenuItem
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ISelectOption } from '../formik-mui';

interface SelectProps extends Omit<OutlinedTextFieldProps, 'variant'> {
  name?: string;
  children?: ReactNode;
  handleClear?: () => void;
  isFullWidth?: boolean;
  variant?: string;
  apiRequest?: () => Promise<any>;
  transformResponse?: (response: any) => ISelectOption[];
  handleOptions?: (data: ISelectOption[]) => void;
  refetch?: Array<any>;
  shouldFetch?: boolean;
}

export const SMBSelect: FC<SelectProps> = ({
  children,
  handleClear,
  isFullWidth,
  apiRequest,
  transformResponse,
  handleOptions,
  refetch = [],
  shouldFetch,
  ...props
}) => {
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await apiRequest();
        const data = transformResponse(response);
        setOptions(data);
        handleOptions && handleOptions(data);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    if (shouldFetch && apiRequest) {
      fetchData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...refetch, shouldFetch]);

  let showClearButton =
    ((props?.SelectProps?.multiple && (props.value as any)?.length > 0) ||
      (!props?.SelectProps?.multiple && props.value)) &&
    !!handleClear;

  const config = {
    fullWidth: isFullWidth ?? true,
    size: 'small' as 'small',
    select: true,
    onChange: props.onChange,
    ...props,
    disabled: isLoading || props.disabled || false,
    label: isLoading ? 'Loading...' : props.label,
    SelectProps: {
      ...props.SelectProps,
      MenuProps: {
        ...props.SelectProps?.MenuProps
      },
      endAdornment: showClearButton && (
        <InputAdornment
          position="end"
          sx={{
            position: 'absolute',
            right: 28,
            cursor: 'pointer'
          }}
        >
          <IconButton
            disabled={props.disabled}
            sx={{ padding: 0 }}
            title="Clear"
            onClick={handleClear}
          >
            <Close fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    }
  };
  return (
    <TextField {...config} variant="outlined">
      {!children && !apiRequest && <MenuItem disabled>No options available</MenuItem>}
      {!children && apiRequest && options?.length === 0 && (
        <MenuItem disabled>No options available</MenuItem>
      )}
      {children}
      {!children &&
        apiRequest &&
        options.map((option, index) => (
          <MenuItem key={`${index}`} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
    </TextField>
  );
};
